import {
  IQboCustomerMappingInput,
  QboCustomerMatchingMode,
  QboSyncCategory,
  QboSyncMode,
} from '@common/interfaces/qbo'
import {
  IQboDocument,
  IQboTransactionsResponse,
  QboDataset,
  QboTransactionSide,
} from '@common/interfaces/qboReporting'
import Base, { Wrapped } from './base'

export interface IQboListTransactionsParams {
  clientId: string
  side: QboTransactionSide
  page?: number
  perPage?: number
  filters?: Record<string, unknown>
  orderBy?: string
  orderDirection?: 'ASC' | 'DESC'
}

export interface IQboGetDocumentsParams {
  // Either one identifies the client.
  clientId?: string
  clientName?: string
  txnType: string
  qboTxnId: string
  documentNumber?: string | null
  refetch?: boolean
}

export interface IQboPullReportDataParams {
  clientId: string
  datasets: QboDataset[]
  asOfDate?: string
  // Per-report depth, keyed by QboReportSettingKey — see QBO_REPORT_SETTINGS.
  periods?: number
  arAgingMonths?: number
  apAgingMonths?: number
  ledgerMonths?: number
  topCounterpartyLimit?: number
  // File the pulled statements as an ongoing reporting submission, the way the
  // Codat portfolio sync does. Ignored when the pull carries neither statement.
  submitOngoingReporting?: boolean
}

export interface IQboSettingsUpdate {
  customerMatchingMode?: QboCustomerMatchingMode
  syncMode?: QboSyncMode
  // The control account: every entry, in every category, posts against it on one
  // side, so it is set once rather than per category.
  loanAccountId?: string
  loanAccountName?: string
  // Per-category update — one row of the settings table at a time. `syncCategory`
  // names the row; the fields below apply to it. Collections writes the
  // pre-existing clearing-account columns; QBO_SYNC_CATEGORY_FIELDS owns that.
  syncCategory?: QboSyncCategory
  isSyncEnabled?: boolean
  syncAccountId?: string
  syncAccountName?: string
  // Remove the category's account. Needed because an omitted `syncAccountId` means
  // "leave it alone" — sending `undefined` on a cleared picker would silently keep
  // the old account and it would reappear on reload.
  clearSyncAccount?: boolean
}

export default class Qbo extends Base {
  getConnectUrl(clientId: string) {
    return this.apiClient.post('qbo/connect-url', { clientId })
  }

  getConnection(clientId: string) {
    return this.apiClient.get('qbo/connection', { clientId })
  }

  updateConnectionSettings(clientId: string, settings: IQboSettingsUpdate) {
    return this.apiClient.put('qbo/connection/settings', { clientId, ...settings })
  }

  listAccounts(clientId: string) {
    return this.apiClient.get('qbo/accounts', { clientId })
  }

  refreshCache(clientId: string) {
    return this.apiClient.post('qbo/cache/refresh', { clientId })
  }

  getCustomerMappings(params: {
    clientId: string
    page?: number
    perPage?: number
    search?: string
    orderBy?: string
    orderDirection?: 'ASC' | 'DESC'
    countOnly?: boolean
  }) {
    return this.apiClient.get('qbo/customer-mappings', params)
  }

  searchCustomers(params: { clientId: string; search?: string; limit?: number }) {
    return this.apiClient.get('qbo/customers/search', params)
  }

  saveCustomerMappings(clientId: string, mappings: IQboCustomerMappingInput[]) {
    return this.apiClient.put('qbo/customer-mappings', { clientId, mappings })
  }

  disconnect(id: string) {
    return this.apiClient.delete(`qbo/connection/${id}`)
  }

  logCollections(data: {
    clientId: string
    recordDateFrom: string
    recordDateTo: string
    activityIds?: string[]
    dryRun?: boolean
  }) {
    return this.apiClient.post('qbo/collections/log', data)
  }

  clearCollections(data: { clientId: string; clearSinceDate?: string; activityTypes?: string[] }) {
    return this.apiClient.post('qbo/collections/clear', data)
  }

  dismissCollections(clientId: string, activityIds: string[]) {
    return this.apiClient.post('qbo/collections/dismiss', { clientId, activityIds })
  }

  pullReportData(data: IQboPullReportDataParams) {
    return this.apiClient.post('qbo/reports/pull', data)
  }

  exportReports(data: { clientId: string; datasets: QboDataset[] }) {
    return this.apiClient.post('qbo/reports/export', data, { isFile: true })
  }

  submitReporting(data: { clientId: string }) {
    return this.apiClient.post('qbo/reports/submit', data)
  }

  listCollectionLog(data: {
    clientId: string
    page?: number
    perPage?: number
    filters?: Record<string, unknown>
    orderBy?: string
    orderDirection?: 'ASC' | 'DESC'
  }) {
    return this.apiClient.get('qbo/collections/log', data)
  }

  listTransactions(params: IQboListTransactionsParams) {
    return this.apiClient.get('qbo/transactions', params) as Wrapped<IQboTransactionsResponse>
  }

  syncTransactions(data: { clientId: string }) {
    return this.apiClient.post('qbo/transactions/sync', data) as Wrapped<{ synced: boolean }>
  }

  getDocuments(data: IQboGetDocumentsParams) {
    return this.apiClient.post('qbo/documents', data) as Wrapped<IQboDocument[]>
  }
}
